import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Briefcase, Wallet, ArrowDownToLine, Clock, X, ShoppingCart, Calendar, User, Users, Truck, ArrowRight } from "lucide-react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectComponentsProvider, ConnectAccountOnboarding } from "@stripe/react-connect-js";
import { api } from "../api";
import { Layout } from "../components/Navbar";
import { formatProName } from "../lib/proName";
import { useNavigate } from "react-router-dom";

const eur = (c) => ((c || 0) / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" });

// Mappa lingua YouPAM -> locale Stripe Connect embedded
const STRIPE_LOCALE = { it: "it-IT", en: "en-US", de: "de-DE", fr: "fr-FR" };
const stripeLocale = (lng) => STRIPE_LOCALE[(lng || "it").split("-")[0]] || "en-US";

export default function AdminPro() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [earn, setEarn] = useState(null);
  const [tx, setTx] = useState([]);
  const [payouts, setPayouts] = useState({ payouts: [], balance: null });
  const [withdrawals, setWithdrawals] = useState([]);
  const [pk, setPk] = useState(null);
  const [month, setMonth] = useState("");
  const [months, setMonths] = useState([]);
  const [period, setPeriod] = useState(null);
  const [earliest, setEarliest] = useState(null);
  const [connectInstance, setConnectInstance] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [matOpen, setMatOpen] = useState(false);
  const [mat, setMat] = useState(null);
  const [matF, setMatF] = useState("all");
  const nav = useNavigate();
  const openMat = async () => { setMatOpen(true); setMat(null); try { const { data } = await api.get("/admin-pro/pending-detail"); setMat(data); } catch { setMat({ items: [], counts: {}, total_cents: 0 }); } };
  useEffect(() => {
    if (!matOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMatOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [matOpen]);

  const load = useCallback(async () => {
    const mq = month ? `?month=${month}` : "";
    try {
      const [s, e, p, c, w] = await Promise.all([
        api.get("/connect/status"), api.get(`/admin-pro/earnings${mq}`),
        api.get("/admin-pro/payouts"), api.get("/connect/config"),
        api.get(`/admin-pro/withdrawals${mq}`),
      ]);
      setStatus(s.data); setEarn(e.data.summary); setTx(e.data.transactions || []);
      setMonths(e.data.months || []); setPeriod(e.data.period || null); setEarliest(e.data.earliest_available || null);
      setPayouts(p.data || { payouts: [] }); setPk(c.data.publishable_key || null);
      setWithdrawals(w.data.withdrawals || []);
    } catch { /* gestito a livello globale */ }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const startOnboarding = useCallback(async () => {
    if (!pk) { toast.error(t("adminpro.no_pk")); return; }
    try {
      const inst = loadConnectAndInitialize({
        publishableKey: pk,
        fetchClientSecret: async () => (await api.post("/connect/account-session")).data.client_secret,
        locale: stripeLocale(i18n.language),
      });
      setConnectInstance(inst); setShowOnboarding(true);
    } catch { toast.error(t("adminpro.onb_error")); }
  }, [pk, t, i18n.language]);

  // Segui la lingua YouPAM anche a componente Stripe già inizializzato
  useEffect(() => {
    if (connectInstance) { try { connectInstance.update({ locale: stripeLocale(i18n.language) }); } catch { /* noop */ } }
  }, [i18n.language, connectInstance]);

  const withdraw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/withdraw");
      if (data.onboarding_required) { toast.info(t("adminpro.withdraw_need_onboard")); await startOnboarding(); }
      else { toast.success(t("adminpro.withdraw_ok", { amount: eur(data.transferred_cents) })); await load(); }
    } catch (e) {
      toast.error(e.response?.data?.detail === "no_available_balance" ? t("adminpro.no_available") : t("adminpro.withdraw_error"));
    } finally { setBusy(false); }
  };

  const bankStatusLabel = (s) => t(`adminpro.${{ in_transit: "bank_in_transit", paid: "bank_paid", failed: "bank_failed" }[s] || "bank_pending"}`);
  const bankStatusColor = (s) => ({ in_transit: "text-amber-600", paid: "text-emerald-600", failed: "text-red-600" }[s] || "text-slate-500");

  const Card = ({ label, value, testid, accent }) => (    <div className="yp-card p-4" data-testid={testid}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-bold ${accent || "text-slate-900"}`}>{value}</div>
    </div>
  );

  return (<Layout><div className="yp-container max-w-4xl py-8" data-testid="adminpro-page">
    <div className="flex items-center gap-2 mb-1"><Briefcase className="text-slate-700" size={22} />
      <h1 className="text-2xl font-bold">{t("adminpro.title")}</h1></div>
    <p className="text-sm text-slate-500 mb-6">{t("adminpro.subtitle")}</p>

    {/* Stato Connect */}
    <div className="yp-card p-4 mb-6 flex items-center justify-between gap-3" data-testid="adminpro-status">
      <div>
        <div className="text-xs text-slate-500">{t("adminpro.connect_status")}</div>
        {status === null ? (
          <div className="h-5 w-28 mt-1 rounded bg-slate-200 animate-pulse" data-testid="adminpro-status-loading" aria-hidden="true" />
        ) : (
          <div className={`font-semibold ${status.onboarded ? "text-emerald-600" : "text-amber-600"}`}>
            {status.onboarded ? t("adminpro.onboarded_yes") : t("adminpro.onboarded_no")}
          </div>
        )}
      </div>
      {status !== null && !status.onboarded && (
        <button className="yp-btn yp-btn-primary" onClick={startOnboarding} data-testid="adminpro-start-onboarding">
          {t("adminpro.start_onboarding")}
        </button>)}
    </div>

    {/* Incassi 95/5 */}
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-2">
      <Card label={t("adminpro.gross")} value={eur(earn?.gross_cents)} testid="adminpro-gross" />
      <Card label={t("adminpro.youpam_fee")} value={eur(earn?.fee_cents)} testid="adminpro-fee" accent="text-slate-500" />
      <Card label={t("adminpro.net95")} value={eur(earn?.net_cents)} testid="adminpro-net" accent="text-emerald-600" />
      {earn?.pending_cents > 0 ? (
        <button onClick={openMat} className="yp-card p-4 text-left cursor-pointer hover:border-amber-400 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-amber-500" data-testid="adminpro-pending" aria-haspopup="dialog">
          <div className="flex items-center justify-between"><div className="text-xs text-slate-500">{t("adminpro.pending")}</div><Clock size={15} className="text-amber-500" /></div>
          <div className="text-xl font-bold text-amber-600">{eur(earn?.pending_cents)}</div>
          <div className="text-xs text-blue-600 mt-0.5 inline-flex items-center gap-0.5" data-testid="adminpro-pending-cta">{t("adminpro.mat.see")} ›</div>
        </button>
      ) : (
        <Card label={t("adminpro.pending")} value={eur(earn?.pending_cents)} testid="adminpro-pending" accent="text-amber-600" />
      )}
      <Card label={t("adminpro.available")} value={eur(earn?.available_cents)} testid="adminpro-available" accent="text-blue-600" />
    </div>
    <p className="text-xs text-slate-400 mb-1">{t("adminpro.note_pending")}</p>
    {earn?.pending_cents > 0 && earliest && (
      <p className="text-xs text-amber-700 mb-1" data-testid="adminpro-available-from">
        {t("adminpro.available_from")}: <b>{new Date(earliest).toLocaleDateString()}</b>
      </p>)}
    <p className="text-xs text-slate-400 mb-4">{t("adminpro.note5")}</p>

    <button className="yp-btn yp-btn-primary mb-6 flex items-center gap-2 disabled:opacity-50"
      onClick={withdraw} disabled={busy || !(earn?.available_cents > 0)} data-testid="adminpro-withdraw">
      <ArrowDownToLine size={18} /> {busy ? t("adminpro.withdrawing") : t("adminpro.withdraw")}
    </button>

    {/* Onboarding embedded */}
    {showOnboarding && connectInstance && (
      <div className="yp-card p-4 mb-6" data-testid="adminpro-onboarding">
        <ConnectComponentsProvider connectInstance={connectInstance}>
          <ConnectAccountOnboarding onExit={() => { setShowOnboarding(false); load(); }} />
        </ConnectComponentsProvider>
      </div>)}

    {/* Saldo & payout */}
    <div className="yp-card p-4 mb-6" data-testid="adminpro-payouts">
      <div className="flex items-center gap-2 mb-2"><Wallet size={18} /><h2 className="font-semibold">{t("adminpro.payouts")}</h2></div>
      {payouts.balance && <div className="text-sm text-slate-600 mb-2">
        {t("adminpro.transferred")}: <b>{eur(earn?.transferred_cents)}</b></div>}
      {(payouts.payouts || []).length === 0
        ? <div className="text-sm text-slate-400">{t("adminpro.empty")}</div>
        : <ul className="text-sm divide-y">{payouts.payouts.map(p => (
            <li key={p.id} className="py-1.5 flex justify-between"><span>{eur(p.amount)}</span>
              <span className="text-slate-500">{p.status}</span></li>))}</ul>}
    </div>

    {/* Filtro periodo */}
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="adminpro-period-filter">
      <span className="text-sm text-slate-500">{t("adminpro.period_label")}</span>
      <select className="border border-slate-200 rounded-md px-2 py-1 text-sm bg-white" value={month}
        onChange={(e) => setMonth(e.target.value)} data-testid="adminpro-month-select">
        <option value="">{t("adminpro.period_all")}</option>
        {months.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      {month && period && (
        <span className="text-sm text-slate-600" data-testid="adminpro-period-earned">
          {t("adminpro.period_earned")}: <b className="text-emerald-600">{eur(period.net_cents)}</b>
        </span>)}
    </div>

    {/* Storico ritiri (trasferimenti già effettuati) */}
    <div className="yp-card p-4 mb-6" data-testid="adminpro-withdrawals">
      <div className="flex items-center gap-2 mb-2"><ArrowDownToLine size={18} /><h2 className="font-semibold">{t("adminpro.withdrawals")}</h2></div>
      {withdrawals.length === 0 ? <div className="text-sm text-slate-400">{t("adminpro.empty")}</div>
        : <ul className="text-sm divide-y">{withdrawals.map(w => (
            <li key={w.transfer_id} className="py-2" data-testid="adminpro-withdrawal-row">
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">{(w.at || "").slice(0, 10)}</span>
                <span className="font-semibold text-blue-600">{eur(w.amount_cents)}</span>
              </div>
              <div className={`text-xs mt-0.5 font-medium ${bankStatusColor(w.bank_status)}`} data-testid="adminpro-bank-status">{bankStatusLabel(w.bank_status)}</div>
              {w.bank_status === "in_transit" && w.bank_arrival_date &&
                <div className="text-xs text-slate-500">{t("adminpro.bank_arrival")}: {new Date(w.bank_arrival_date).toLocaleDateString()}</div>}
              {(!w.bank_status || w.bank_status === "pending_bank") &&
                <div className="text-xs text-slate-400 mt-0.5">{t("adminpro.bank_pending_msg")}</div>}
              {w.bank_status === "failed" &&
                <div className="text-xs text-red-500 mt-0.5">{t("adminpro.bank_failed_msg")}</div>}
            </li>))}</ul>}
    </div>

    {/* Storico transazioni */}
    <div className="yp-card p-4" data-testid="adminpro-transactions">
      <h2 className="font-semibold mb-2">{t("adminpro.tx_history")}</h2>
      {tx.length === 0 ? <div className="text-sm text-slate-400">{t("adminpro.empty")}</div>
        : <ul className="text-sm divide-y">{tx.map(r => (
            <li key={r.id} className="py-1.5 flex justify-between gap-2">
              <span className="text-slate-500">{(r.at || "").slice(0, 10)}</span>
              <span>{eur(r.gross_cents)}</span>
              <span className="text-emerald-600">+{eur(r.net_cents)}</span>
              <span className="text-xs text-slate-400">{r.status}</span>
            </li>))}</ul>}
    </div>
    {matOpen && <MatModal mat={mat} filter={matF} setFilter={setMatF} onClose={() => setMatOpen(false)} t={t} i18nLang={i18n.language} nav={nav} eur={eur} />}
  </div></Layout>);
}

function MatModal({ mat, filter, setFilter, onClose, t, i18nLang, nav, eur }) {
  const items = mat?.items || [];
  const shown = items.filter((i) => filter === "all"
    || (filter === "service" && i.kind === "bookable_service")
    || (filter === "event" && i.kind === "paid_event")
    || (filter === "shop" && i.kind === "product_sale"));
  const c = mat?.counts || {};
  const dt = (s) => (s ? new Date(s).toLocaleString(i18nLang) : "");
  const reasonText = (i) => {
    if (i.reason === "svc_pro" && i.pro_name) return t("adminpro.mat.r_svc_pro", { pro: formatProName({ title: i.pro_title, name: i.pro_name }, i18nLang) });
    if (i.reason === "svc_org" || i.reason === "svc_pro") return t("adminpro.mat.r_svc_org");
    if (i.reason === "evt_end") return t("adminpro.mat.r_evt_end");
    if (i.reason === "shop_shipped") return t("adminpro.mat.r_shop_shipped");
    if (i.reason === "shop_wait") return t("adminpro.mat.r_shop_wait");
    return null;
  };
  const badge = (k) => k === "bookable_service" ? t("adminpro.mat.services") : k === "paid_event" ? t("adminpro.mat.events") : k === "product_sale" ? t("adminpro.mat.shop") : "—";
  const Icon = ({ k }) => k === "bookable_service" ? <User size={16} /> : k === "paid_event" ? <Calendar size={16} /> : <ShoppingCart size={16} />;
  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="mat-overlay">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl flex flex-col" style={{ maxHeight: "88vh" }} onClick={(e) => e.stopPropagation()} data-testid="mat-modal" role="dialog" aria-modal="true">
        <div className="sticky top-0 bg-white px-5 pt-5 pb-3 border-b border-slate-100 rounded-t-2xl z-10">
          <button onClick={onClose} className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500" data-testid="mat-close" aria-label={t("shop.close")}><X size={22} /></button>
          <h3 className="text-xl font-bold pr-12" data-testid="mat-title">{t("adminpro.mat.title")}</h3>
          <p className="text-sm text-slate-500 mt-1 pr-8">{t("adminpro.mat.subtitle")}</p>
        </div>
        <div className="overflow-y-auto px-5 py-4 flex-1" data-testid="mat-scroll">
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2 mb-3">
            <Clock size={18} className="text-amber-500 mt-0.5 shrink-0" />
            <div><div className="font-semibold text-slate-800" data-testid="mat-total">{t("adminpro.mat.total")}: {eur(mat?.total_cents)} · {t("adminpro.mat.ops", { count: mat?.count || 0 })}</div>
              <div className="text-xs text-slate-500">{t("adminpro.mat.total_hint")}</div></div>
          </div>
          <div className="flex gap-2 flex-wrap mb-3" data-testid="mat-filters">
            {[["all", t("adminpro.mat.all"), c.all], ["service", t("adminpro.mat.services"), c.service], ["event", t("adminpro.mat.events"), c.event], ["shop", t("adminpro.mat.shop"), c.shop]].map(([k, lbl, n]) => (
              <button key={k} onClick={() => setFilter(k)} className={`yp-btn !py-1 !px-3 text-sm ${filter === k ? "yp-btn-primary" : "yp-btn-outline"}`} data-testid={`mat-filter-${k}`}>{lbl} ({n || 0})</button>))}
          </div>
          {!mat ? <div className="text-slate-400 py-6 text-center">{t("common.loading")}</div>
            : shown.length === 0 ? <div className="text-slate-400 py-8 text-center" data-testid="mat-empty">{t("adminpro.mat.empty")}</div>
            : shown.map((i) => (
              <div key={i.ledger_id} className="border border-slate-200 rounded-xl p-4 mb-3" data-testid="mat-item">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"><Icon k={i.kind} /></span>
                    <span className="yp-badge yp-badge-approved text-xs">{badge(i.kind)}</span>
                    {i.status && <span className="yp-badge yp-badge-paid text-xs">{t(`shop.st_${i.status}`, i.status)}</span>}
                  </div>
                  <div className="text-right"><div className="font-bold" data-testid="mat-item-net">{eur(i.net_cents)}</div><div className="text-[11px] text-slate-400">{t("adminpro.mat.net")}</div></div>
                </div>
                <div className="font-semibold mt-2">{i.matched ? i.title : t("adminpro.mat.unmatched")}</div>
                {!i.matched && <div className="text-xs text-slate-500">Ref: {i.ref_id}</div>}
                {i.kind === "product_sale" && i.product && <div className="text-sm text-slate-600">{i.product}{i.more > 0 ? ` · ${t("shop.more_items", { count: i.more })}` : ""}</div>}
                <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                  {i.pro_name && <div className="flex items-center gap-1"><User size={13} /> {formatProName({ title: i.pro_title, name: i.pro_name }, i18nLang)}</div>}
                  {i.patient && <div className="flex items-center gap-1"><Users size={13} /> {i.patient}</div>}
                  {i.customer && <div className="flex items-center gap-1"><User size={13} /> {i.customer}</div>}
                  {i.tracking && <div className="flex items-center gap-1"><Truck size={13} /> {i.tracking}</div>}
                  {i.when && <div className="flex items-center gap-1"><Calendar size={13} /> {dt(i.when)}</div>}
                </div>
                {reasonText(i) && <div className="bg-amber-50 rounded-lg px-3 py-2 mt-2 text-xs text-amber-800 flex items-start gap-1.5" data-testid="mat-reason"><Clock size={13} className="mt-0.5 shrink-0" />{reasonText(i)}</div>}
                {i.matched && i.link && <button onClick={() => { onClose(); nav(i.link); }} className="yp-btn yp-btn-outline !py-1 !px-3 text-xs mt-2 inline-flex items-center gap-1" data-testid="mat-open-link">{i.kind === "paid_event" ? t("adminpro.mat.open_event") : i.kind === "product_sale" ? t("adminpro.mat.open_order") : t("adminpro.mat.open_booking")} <ArrowRight size={13} /></button>}
              </div>))}
        </div>
        <div className="border-t border-slate-100 p-3"><button onClick={onClose} className="yp-btn yp-btn-ghost w-full" data-testid="mat-close-footer">{t("shop.close")}</button></div>
      </div>
    </div>);
}
